import dayjs, { Dayjs, OpUnitType, QUnitType } from 'dayjs'
import 'dayjs/locale/ko'

export interface CookieSetOptions {
  path?: string
  expires?: Date
  maxAge?: number
  domain?: string
  secure?: boolean
  httpOnly?: boolean
  sameSite?: boolean | 'none' | 'lax' | 'strict'
  partitioned?: boolean
}

const now = () => dayjs().locale('ko')
const A_HOUR = 24
const SECONDS_IN_A_MINUTES = 60
const SECONDS_IN_A_HOUR = 3600
const SECONDS_IN_A_DAY = A_HOUR * SECONDS_IN_A_HOUR

export type DateFormatType =
  | 'YY.MM.DD'
  | 'YY-MM-DD'
  | 'YY/MM/DD'
  | 'YYYY/MM/DD'
  | 'YYYY.MM.DD'
  | 'YYYY년 MM월 DD일'
  | 'YYYYMMDDHHmmss'
  | 'YYMMDD'
  | 'YYYY-MM-DD'

const removeDateStringTZ = (date: string) =>
  dayjs(date?.replace(/-/g, '/').replace('T', ' ').replace('Z', ' '))

const DateFormat = (date: string | Dayjs, format: DateFormatType): string => {
  if (!date) return ''
  const targetDate = typeof date === 'string' ? removeDateStringTZ(date) : date

  return targetDate?.format(format)
}

function getDayDifference(date: string | Dayjs, unit: QUnitType | OpUnitType): string {
  if (!date) return '0'
  const targetDate = typeof date === 'string' ? removeDateStringTZ(date) : date
  const nowDate = unit === 'days' ? DateFormat(now(), 'YYYY-MM-DD') : now()
  const difResultValue = targetDate.locale('ko').diff(nowDate, unit)
  return difResultValue === 0 ? 'DAY' : `${difResultValue}`
}

function expiration7Days(date: string): boolean {
  return Number(getDayDifference(date, 'days')) > 7
}

function expiration1Days(date: string): boolean {
  return Number(getDayDifference(date, 'day')) > 0
}

function isValidInDays(date: string, expireDay: number): boolean {
  return !(Number(getDayDifference(dayjs(date).startOf('day'), 'days')) < -1 * expireDay)
}

function isValidInYears(date: string, expireYear: number): boolean {
  return !(Number(getDayDifference(dayjs(date).startOf('day'), 'years')) < -1 * expireYear + 1)
}

function getAfter7Days(date: string | Dayjs) {
  const targetDate = typeof date === 'string' ? dayjs(date).add(-9, 'hours') : date
  return targetDate.add(7, 'days')
}

function timeFormatter(count: number) {
  const days = Math.floor(count / SECONDS_IN_A_DAY)

  const remainingSeconds = count % SECONDS_IN_A_DAY
  const hours = Math.floor(remainingSeconds / SECONDS_IN_A_HOUR)
  const minutes = Math.floor((remainingSeconds % SECONDS_IN_A_HOUR) / SECONDS_IN_A_MINUTES)
  const seconds = remainingSeconds % SECONDS_IN_A_MINUTES

  const formattedDays = `${days < 10 && '0'}${days}`
  const formattedHours = hours.toString().padStart(2, '0')
  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = seconds.toString().padStart(2, '0')

  if (days >= 1) {
    return `${formattedDays}`
  } else {
    return `${formattedHours} : ${formattedMinutes} : ${formattedSeconds}`
  }
}

const cookieExpireSessionTime = () => now().add(6, 'months').toDate()
const cookieJoinExpireSessionTime = () => now().add(10, 'minutes').toDate()
const cookieNonePinCheckTime = () => now().add(40, 'seconds').toDate() //24시간 미만
const cookieNoneAppInstallPopupTime = () => now().add(10, 'seconds').toDate()
const cookieRestTodayTime = () => now().endOf('day').toDate()

const joinExpireSessionTimeCount = 10 * 60 //10분(cookieJoinExpireSessionTime)

const sessionCookieOptions = () => {
  return {
    path: '/',
    expires: cookieExpireSessionTime(),
  } as CookieSetOptions
}

export const DateUtils = {
  expiration7Days,
  timeFormatter,
  getDayDifference,
  DateFormat,
  now,
  expiration1Days,
  removeDateStringTZ,
  SECONDS_IN_A_DAY,
  cookieExpireSessionTime,
  cookieJoinExpireSessionTime,
  cookieNonePinCheckTime,
  cookieNoneAppInstallPopupTime,
  cookieRestTodayTime,
  sessionCookieOptions,
  isValidInDays,
  isValidInYears,
  getAfter7Days,
  joinExpireSessionTimeCount,
}
